import { ReactNode, useState } from 'react';
import { useReduxState } from 'hooks';
import { FlagIcon, Expand, RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { useEditorContext } from '.';

interface Props {
  backgroundColor?: string;
  children: ReactNode;
  color?: string;
  isCode?: boolean;
  isEnabled?: boolean;
  isInnerElement?: boolean;
  referenceHtml: string;
  style?: React.CSSProperties;
  tableLayout?: 'auto' | 'fixed';
  translationHtml: string;
}

const EditorBlockTranslationReference = ({
  backgroundColor = '#ffffff',
  children,
  color,
  isCode = false,
  isEnabled = true,
  isInnerElement = false,
  referenceHtml,
  style,
  tableLayout,
  translationHtml
}: Props) => {
  const {
    editorActiveLanguageVersion,
    editorTranslationReferenceLanguage,
    editorPreviewType
  } = useEditorContext();

  const { messages, theme } = useReduxState();

  const [isExpanded, setIsExpanded] = useState(false);

  // do not render when not translating a language version
  if (
    !isEnabled ||
    !editorActiveLanguageVersion ||
    !editorTranslationReferenceLanguage ||
    editorPreviewType
  ) {
    return children;
  }

  const isReferenceEmpty =
    !referenceHtml ||
    referenceHtml.trim() === '' ||
    referenceHtml.trim() === '<p></p>';

  const isTranslationEmpty =
    !translationHtml ||
    translationHtml.trim() === '' ||
    translationHtml.trim() === '<p></p>';

  const lineHeight = 21;

  return (
    <div
      className={`reference ${
        isInnerElement ? 'reference-inner' : 'reference-non-inner'
      } ${
        !isReferenceEmpty && isTranslationEmpty
          ? 'reference-without-translation'
          : ''
      }`}
      style={style}
    >
      <div className="header">
        <Expand defaultMaxHeight={lineHeight * 2} isExpanded={isExpanded}>
          <div className="content">
            <div className="content-flag">
              <FlagIcon
                lang={editorTranslationReferenceLanguage}
                size="small"
              />
            </div>
            <div className="content-value">
              {isReferenceEmpty ? (
                <em className="content-empty">{messages.commonNoContent}</em>
              ) : isCode ? (
                <pre className="content-pre">
                  <code>{referenceHtml}</code>
                </pre>
              ) : tableLayout ? (
                <table
                  className={`content-table ${
                    tableLayout === 'fixed' ? 'content-table-fixed' : ''
                  }`}
                >
                  <tbody dangerouslySetInnerHTML={{ __html: referenceHtml }} />
                </table>
              ) : (
                <RichTextValue html={referenceHtml} />
              )}
            </div>
          </div>

          <div
            className={`toggle ${
              isExpanded ? 'toggle-expanded' : 'toggle-not-expanded'
            }`}
            style={
              isExpanded ? {} : { height: lineHeight, top: lineHeight + 6 }
            }
          >
            <button
              className="toggle-button"
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ backgroundColor, top: -9 }}
            >
              <div className="toggle-text">
                {isExpanded ? messages.commonShowLess : messages.commonShowMore}
              </div>
              <div className="toggle-icon">
                <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
              </div>
            </button>
          </div>
        </Expand>
      </div>

      {children}

      <style jsx>{`
        .reference {
          position: relative;
        }
        .reference-inner {
          border: 1px dashed ${theme.lightGreyDarkActive};
          border-radius: ${theme.inputBorderRadius};
          padding: 5px;
        }
        .reference-inner.reference-without-translation {
          border-color: ${theme.warningStandaloneColor};
        }
        .header {
          overflow: hidden;
          position: relative;
        }
        .reference-inner .header {
          margin-bottom: 5px;
        }
        .reference-non-inner .header {
          margin-bottom: 10px;
        }
        .content {
          align-items: flex-start;
          display: flex;
          font-size: 1.4rem;
          gap: 8px;
        }
        .content :global(ol),
        .content :global(ul) {
          margin: 0;
          padding-left: 20px;
        }
        .content-empty {
          color: ${theme.textColorLighter};
        }
        .content-flag {
          flex-grow: 0;
          flex-shrink: 0;
          position: relative;
          top: -1px;
        }
        .content-value {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .content-pre {
          white-space: pre-wrap;
          overflow-wrap: break-word;
        }
        .content-table {
          border-collapse: collapse;
          width: 100%;
        }
        .content-table-fixed {
          table-layout: fixed;
        }
        .content-table :global(td) {
          border: 1px dashed ${theme.lightGreyDark};
          padding: 4px 8px;
        }
        .toggle {
          background: linear-gradient(
            to bottom,
            rgb(from ${backgroundColor} r g b / 0.7),
            rgb(from ${backgroundColor} r g b / 1)
          );
          text-align: center;
        }
        .toggle-expanded {
          margin-top: 10px;
        }
        .toggle-not-expanded {
          height: 21px;
          left: 0;
          position: absolute;
          right: 0;
          top: 27px;
        }
        .toggle-button {
          align-items: center;
          border: none;
          border-radius: ${theme.buttonBorderRadius};
          color: ${color || theme.textColorLight};
          cursor: pointer;
          display: inline-flex;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 5px;
          line-height: 0;
          outline: 0;
          padding: 2px 5px 3px;
          position: relative;
          top: -3px;
        }
        .toggle-button:hover {
          text-decoration: underline;
        }
        .toggle-icon {
          font-size: 1.2rem;
          position: relative;
          top: 1px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockTranslationReference;
